import React, { useState } from 'react';
import useDateRangeCol from './useDateRangeCol';
import { Tooltip, Typography, IconButton, Box } from '@mui/material';
import { ContentCopy, Check } from '@mui/icons-material';

const CommentTooltipContent = ({ value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
          Comment
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {copied && (
            <Typography variant="caption" sx={{ color: 'green', fontSize: '0.7rem' }}>
              Copied!
            </Typography>
          )}
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{
              color: 'black',
              padding: '4px',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
            title="Copy to clipboard"
          >
            {copied ? <Check fontSize="small" sx={{ color: 'green' }} /> : <ContentCopy fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
      <div
        style={{
          maxHeight: '300px',
          overflowY: 'auto',
          overflowX: 'hidden',
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
        }}
      >
        {value}
      </div>
    </Box>
  );
};

const useCommentCols = () => {

    const createDateCol = useDateRangeCol("CRTDATE", "Create Date");
    const modifyDateCol = useDateRangeCol("MODDATE", "Modify Date");

  const commentCols = [
    {
      accessorKey: 'NAME',
      header: 'Name',
    },
    {
      accessorKey: 'TYPE',
      header: 'Type',
    },
    createDateCol,
    {
      accessorKey: 'CRTTIME',
      header: 'Create Time',
    },
    modifyDateCol,
    {
      accessorKey: 'MODTIME',
      header: 'Modify Time',
    },
    {
      accessorKey: 'COMMENT',
      header: 'Comment',
      Cell: ({ cell }) => {
        const value = cell.getValue();
        if (!value) return '';
        
        const maxLength = 50;
        const isTruncated = value.length > maxLength;
        const displayText = isTruncated ? value.substring(0, maxLength) + '...' : value;
        
        if (isTruncated) {
          return (
            <Tooltip
              title={<CommentTooltipContent value={value} />}
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'white',
                    color: 'black',
                    border: '1px solid #e0e0e0',
                    fontSize: '0.875rem',
                    maxWidth: '500px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    padding: '8px 12px',
                  },
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  cursor: 'help',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {displayText}
              </Typography>
            </Tooltip>
          );
        }
        
        return <Typography variant="body2">{displayText}</Typography>;
      },
    },
  ];

  return commentCols;
}

const useLandmarkCommentCols = () => {

const landmarkCommentCols = [
  {
    accessorKey: 'FIELD_DISPLAY',
    header: 'Field',
  },
  {
    accessorKey: 'FIELD_VALUE',
    header: 'Value',
  }
];

return landmarkCommentCols;
}

export { useLandmarkCommentCols, useCommentCols };
